import { supabase } from '@/lib/supabase';

/**
 * 현재 인증된 판매자의 레스토랑 ID를 가져옵니다
 */
export async function getSellerRestaurantId(): Promise<{
  success: boolean;
  restaurantId?: string;
  error?: string;
}> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      return {
        success: false,
        error: '로그인이 필요합니다'
      };
    }

    // 사용자의 레스토랑 찾기
    const { data: restaurant, error: restaurantError } = await supabase
      .from('restaurants')
      .select('id')
      .eq('owner_id', user.id)
      .single();

    if (restaurantError) {
      console.error('Restaurant fetch error:', restaurantError);
      return {
        success: false,
        error: '등록된 레스토랑을 찾을 수 없습니다'
      };
    }

    return {
      success: true,
      restaurantId: restaurant.id
    };
  } catch (error) {
    console.error('Get seller restaurant ID error:', error);
    return {
      success: false,
      error: '레스토랑 정보를 가져오는 중 오류가 발생했습니다'
    };
  }
}

/**
 * 판매자 인증 상태를 확인합니다
 */
export async function checkSellerAuth(): Promise<{
  success: boolean;
  isAuthenticated: boolean;
  userId?: string;
  error?: string;
}> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return {
        success: true,
        isAuthenticated: false,
        error: '인증되지 않은 사용자입니다'
      };
    }

    // 판매자 역할 확인
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('user_role')
      .eq('id', user.id)
      .single();

    if (userError || userData?.user_role !== 'seller') {
      return {
        success: true,
        isAuthenticated: false,
        error: '판매자 권한이 없습니다'
      };
    }

    return {
      success: true,
      isAuthenticated: true,
      userId: user.id
    };
  } catch (error) {
    console.error('Check seller auth error:', error);
    return {
      success: false,
      isAuthenticated: false,
      error: '인증 확인 중 오류가 발생했습니다'
    };
  }
}

/**
 * 판매자의 레스토랑 접근 권한을 검증합니다
 */
export async function validateSellerAccess(restaurantId: string): Promise<{
  success: boolean;
  hasAccess: boolean;
  error?: string;
}> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      return {
        success: false,
        hasAccess: false,
        error: '로그인이 필요합니다'
      };
    }

    // 해당 레스토랑의 소유자인지 확인
    const { data: restaurant, error: restaurantError } = await supabase
      .from('restaurants')
      .select('owner_id')
      .eq('id', restaurantId)
      .single();

    if (restaurantError) {
      console.error('Restaurant validation error:', restaurantError);
      return {
        success: false,
        hasAccess: false,
        error: '레스토랑을 찾을 수 없습니다'
      };
    }

    const hasAccess = restaurant.owner_id === user.id;

    return {
      success: true,
      hasAccess,
      error: hasAccess ? undefined : '해당 레스토랑에 대한 권한이 없습니다'
    };
  } catch (error) {
    console.error('Validate seller access error:', error);
    return {
      success: false,
      hasAccess: false,
      error: '권한 검증 중 오류가 발생했습니다'
    };
  }
}